import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './AdminSettings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    general: {
      siteName: 'Project Portfolio Manager',
      siteUrl: 'https://ppm.example.com',
      supportEmail: 'support@ppm.com',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD'
    },
    email: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: '587',
      smtpUser: '',
      smtpPassword: '',
      fromEmail: 'noreply@ppm.com',
      fromName: 'PPM System'
    },
    security: {
      sessionTimeout: '30',
      passwordMinLength: '8',
      requireSpecialChar: true,
      requireNumber: true,
      requireUppercase: true,
      twoFactorEnabled: false,
      maxLoginAttempts: '5'
    },
    notifications: {
      emailNotifications: true,
      demoRequestNotifications: true,
      newOrganizationNotifications: true,
      paymentNotifications: true,
      systemAlerts: true
    },
    maintenance: {
      maintenanceMode: false,
      maintenanceMessage: 'System is under maintenance. Please check back later.',
      allowedIPs: ''
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/${API_VERSION}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.data) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use default settings if API fails
    }
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async (section) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_URL}/api/${API_VERSION}/admin/settings/${section}`,
        settings[section],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const renderGeneralSettings = () => (
    <div className="settings-section">
      <h2>General Settings</h2>
      <div className="settings-form">
        <div className="form-group">
          <label>Site Name</label>
          <input
            type="text"
            value={settings.general.siteName}
            onChange={(e) => handleInputChange('general', 'siteName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Site URL</label>
          <input
            type="url"
            value={settings.general.siteUrl}
            onChange={(e) => handleInputChange('general', 'siteUrl', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Support Email</label>
          <input
            type="email"
            value={settings.general.supportEmail}
            onChange={(e) => handleInputChange('general', 'supportEmail', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Timezone</label>
          <select
            value={settings.general.timezone}
            onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
            <option value="Asia/Kolkata">India Standard Time</option>
          </select>
        </div>
        <div className="form-group">
          <label>Date Format</label>
          <select
            value={settings.general.dateFormat}
            onChange={(e) => handleInputChange('general', 'dateFormat', e.target.value)}
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
        <div className="form-group">
          <label>Currency</label>
          <select
            value={settings.general.currency}
            onChange={(e) => handleInputChange('general', 'currency', e.target.value)}
          >
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="INR">INR - Indian Rupee</option>
          </select>
        </div>
        <button className="btn-save" onClick={() => handleSave('general')} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="settings-section">
      <h2>Email Configuration</h2>
      <div className="settings-form">
        <div className="form-group">
          <label>SMTP Host</label>
          <input
            type="text"
            value={settings.email.smtpHost}
            onChange={(e) => handleInputChange('email', 'smtpHost', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>SMTP Port</label>
          <input
            type="text"
            value={settings.email.smtpPort}
            onChange={(e) => handleInputChange('email', 'smtpPort', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>SMTP Username</label>
          <input
            type="text"
            value={settings.email.smtpUser}
            onChange={(e) => handleInputChange('email', 'smtpUser', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>SMTP Password</label>
          <input
            type="password"
            value={settings.email.smtpPassword}
            onChange={(e) => handleInputChange('email', 'smtpPassword', e.target.value)}
            placeholder="••••••••"
          />
        </div>
        <div className="form-group">
          <label>From Email</label>
          <input
            type="email"
            value={settings.email.fromEmail}
            onChange={(e) => handleInputChange('email', 'fromEmail', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>From Name</label>
          <input
            type="text"
            value={settings.email.fromName}
            onChange={(e) => handleInputChange('email', 'fromName', e.target.value)}
          />
        </div>
        <button className="btn-save" onClick={() => handleSave('email')} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h2>Security Settings</h2>
      <div className="settings-form">
        <div className="form-group">
          <label>Session Timeout (minutes)</label>
          <input
            type="number"
            value={settings.security.sessionTimeout}
            onChange={(e) => handleInputChange('security', 'sessionTimeout', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Password Minimum Length</label>
          <input
            type="number"
            value={settings.security.passwordMinLength}
            onChange={(e) => handleInputChange('security', 'passwordMinLength', e.target.value)}
          />
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.security.requireSpecialChar}
              onChange={(e) => handleInputChange('security', 'requireSpecialChar', e.target.checked)}
            />
            Require Special Character
          </label>
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.security.requireNumber}
              onChange={(e) => handleInputChange('security', 'requireNumber', e.target.checked)}
            />
            Require Number
          </label>
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.security.requireUppercase}
              onChange={(e) => handleInputChange('security', 'requireUppercase', e.target.checked)}
            />
            Require Uppercase Letter
          </label>
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.security.twoFactorEnabled}
              onChange={(e) => handleInputChange('security', 'twoFactorEnabled', e.target.checked)}
            />
            Enable Two-Factor Authentication
          </label>
        </div>
        <div className="form-group">
          <label>Max Login Attempts</label>
          <input
            type="number"
            value={settings.security.maxLoginAttempts}
            onChange={(e) => handleInputChange('security', 'maxLoginAttempts', e.target.value)}
          />
        </div>
        <button className="btn-save" onClick={() => handleSave('security')} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h2>Notification Settings</h2>
      <div className="settings-form">
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.emailNotifications}
              onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
            />
            Enable Email Notifications
          </label>
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.demoRequestNotifications}
              onChange={(e) => handleInputChange('notifications', 'demoRequestNotifications', e.target.checked)}
            />
            Demo Request Notifications
          </label>
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.newOrganizationNotifications}
              onChange={(e) => handleInputChange('notifications', 'newOrganizationNotifications', e.target.checked)}
            />
            New Organization Notifications
          </label>
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.paymentNotifications}
              onChange={(e) => handleInputChange('notifications', 'paymentNotifications', e.target.checked)}
            />
            Payment Notifications
          </label>
        </div>
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.systemAlerts}
              onChange={(e) => handleInputChange('notifications', 'systemAlerts', e.target.checked)}
            />
            System Alerts
          </label>
        </div>
        <button className="btn-save" onClick={() => handleSave('notifications')} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const renderMaintenanceSettings = () => (
    <div className="settings-section">
      <h2>Maintenance Mode</h2>
      <div className="settings-form">
        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={settings.maintenance.maintenanceMode}
              onChange={(e) => handleInputChange('maintenance', 'maintenanceMode', e.target.checked)}
            />
            Enable Maintenance Mode
          </label>
          <small>When enabled, only admins can access the system</small>
        </div>
        <div className="form-group">
          <label>Maintenance Message</label>
          <textarea
            rows="3"
            value={settings.maintenance.maintenanceMessage}
            onChange={(e) => handleInputChange('maintenance', 'maintenanceMessage', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Allowed IP Addresses (comma-separated)</label>
          <textarea
            rows="2"
            value={settings.maintenance.allowedIPs}
            onChange={(e) => handleInputChange('maintenance', 'allowedIPs', e.target.value)}
            placeholder="192.168.1.1, 10.0.0.1"
          />
          <small>Leave empty to allow all IPs during maintenance</small>
        </div>
        <button className="btn-save" onClick={() => handleSave('maintenance')} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="admin-settings">
      <div className="page-header">
        <h1>System Settings</h1>
        <p>Configure system-wide settings and preferences</p>
      </div>

      <div className="settings-container">
        <div className="settings-tabs">
          <button
            className={activeTab === 'general' ? 'active' : ''}
            onClick={() => setActiveTab('general')}
          >
            <i className="fas fa-cog"></i>
            General
          </button>
          <button
            className={activeTab === 'email' ? 'active' : ''}
            onClick={() => setActiveTab('email')}
          >
            <i className="fas fa-envelope"></i>
            Email
          </button>
          <button
            className={activeTab === 'security' ? 'active' : ''}
            onClick={() => setActiveTab('security')}
          >
            <i className="fas fa-shield-alt"></i>
            Security
          </button>
          <button
            className={activeTab === 'notifications' ? 'active' : ''}
            onClick={() => setActiveTab('notifications')}
          >
            <i className="fas fa-bell"></i>
            Notifications
          </button>
          <button
            className={activeTab === 'maintenance' ? 'active' : ''}
            onClick={() => setActiveTab('maintenance')}
          >
            <i className="fas fa-tools"></i>
            Maintenance
          </button>
        </div>

        <div className="settings-content">
          {activeTab === 'general' && renderGeneralSettings()}
          {activeTab === 'email' && renderEmailSettings()}
          {activeTab === 'security' && renderSecuritySettings()}
          {activeTab === 'notifications' && renderNotificationSettings()}
          {activeTab === 'maintenance' && renderMaintenanceSettings()}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;


